import React, { createContext, useContext, useState, useEffect } from 'react'

interface AuthContextType {
  isAuthenticated: boolean
  login: (password: string) => Promise<boolean>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Admin authentication now handled via backend API for security

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Enhanced authentication check with multiple validations
  const validateAuthentication = () => {
    try {
      const authStatus = sessionStorage.getItem('admin_authenticated')
      const authTimestamp = sessionStorage.getItem('admin_auth_timestamp')
      
      // Check if authentication exists and is not expired (1 hour)
      if (authStatus === 'true' && authTimestamp) {
        const timestamp = parseInt(authTimestamp)
        const now = Date.now()
        const oneHour = 60 * 60 * 1000 // 1 hour in milliseconds
        
        if (now - timestamp < oneHour) {
          return true
        } else {
          // Session expired, clear authentication
          sessionStorage.removeItem('admin_authenticated')
          sessionStorage.removeItem('admin_auth_timestamp')
          return false
        }
      }
      return false
    } catch (error) {
      // Handle authentication error silently
      return false
    }
  }

  useEffect(() => {
    // Validate authentication on every app load
    const checkAuth = () => {
      const isValid = validateAuthentication()
      setIsAuthenticated(isValid)
      setIsLoading(false)
    }
    
    // Small delay to ensure sessionStorage is available
    const timeoutId = setTimeout(checkAuth, 100)
    
    return () => clearTimeout(timeoutId)
  }, [])

  const login = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      })

      const data = await response.json()

      if (data.success) {
        const timestamp = Date.now().toString()
        setIsAuthenticated(true)
        sessionStorage.setItem('admin_authenticated', 'true')
        sessionStorage.setItem('admin_auth_timestamp', timestamp)
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error('Authentication error:', error)
      return false
    }
  }

  const logout = () => {
    setIsAuthenticated(false)
    // Clear all authentication data
    sessionStorage.removeItem('admin_authenticated')
    sessionStorage.removeItem('admin_auth_timestamp')
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
